import { questTemplates, type QuestTemplate } from '../data/npcs';
import { itemDef } from '../data/items';
import { creatures } from '../data/creatures';
import { mulberry32, seedAt, randInt, weightedPick } from '../core/Rng';
import type { Player } from '../player/Player';
import type { World } from '../world/World';

/**
 * Günlük görev sistemi.
 * BEDO her OYUN GÜNÜ 3 görev sunar. Görevler (seed + gün) ile deterministiktir:
 * aynı güne dönüldüğünde aynı görevler gelir, yenilemek için gün geçmesi gerekir.
 *
 * İlerleme türe göre farklı izlenir:
 *  - gather/mine : envanterdeki miktar (teslimde düşülür)
 *  - hunt        : oyun içi sayaç (avlanan canlı)
 *  - fish        : oyun içi sayaç
 *  - craft       : envanterdeki üretilen eşya
 *  - explore     : keşfedilen chunk sayısı (görev başındaki farkı)
 */

export interface ActiveQuest {
  id: string;
  template: string;
  kind: QuestTemplate['kind'];
  target: string;
  amount: number;
  text: string;
  rewardKogox: number;
  rewardItems: Array<{ item: string; count: number }>;
  done: boolean;
  /** hunt/fish gibi sayaç tabanlı görevler için ilerleme. */
  counter: number;
  /** explore için başlangıç referansı. */
  baseline: number;
}

export interface QuestState {
  day: number;
  quests: ActiveQuest[];
  completedToday: number;
}

const DAILY_COUNT = 3;

export class QuestSystem {
  state: QuestState = { day: 0, quests: [], completedToday: 0 };

  constructor(private world: World) {}

  /** Gün değişince görevleri yenile. */
  ensureDay(day: number, discovered: number): void {
    if (this.state.day === day && this.state.quests.length > 0) return;
    this.state = { day, quests: this.generate(day, discovered), completedToday: 0 };
  }

  private generate(day: number, discovered: number): ActiveQuest[] {
    const rand = mulberry32(seedAt(this.world.params.seed, day, 4242, 9));
    const pool = questTemplates.all().map((t) => ({ weight: 1, value: t }));
    const out: ActiveQuest[] = [];
    const used = new Set<string>();
    for (let i = 0; i < 40 && out.length < DAILY_COUNT; i++) {
      const t = weightedPick(rand, pool);
      if (!t || used.has(t.id)) continue;
      used.add(t.id);
      const target = t.targets[Math.floor(rand() * t.targets.length)] ?? t.targets[0]!;
      const amount = randInt(rand, t.amountMin, t.amountMax);
      const kogox = randInt(rand, t.kogox[0], t.kogox[1]);
      const rewardItems: Array<{ item: string; count: number }> = [];
      for (const r of t.rewards ?? []) {
        if (rand() <= r.chance) rewardItems.push({ item: r.item, count: randInt(rand, r.min, r.max) });
      }
      const targetName = t.kind === 'hunt' ? (creatures.get(target)?.name ?? target) : t.kind === 'explore' ? 'bölge' : itemDef(target).name;
      out.push({
        id: `q${day}_${out.length}`,
        template: t.id,
        kind: t.kind,
        target,
        amount,
        text: t.text.replace('{amount}', `${amount} ${targetName}`),
        rewardKogox: kogox,
        rewardItems,
        done: false,
        counter: 0,
        baseline: t.kind === 'explore' ? discovered : 0,
      });
    }
    return out;
  }

  /** Av/balık sayaçlarını ilerletir. */
  track(kind: 'hunt' | 'fish', target: string): void {
    for (const q of this.state.quests) {
      if (q.done || q.kind !== kind) continue;
      if (q.target === target || q.kind === 'fish') q.counter++;
    }
  }

  /** Görevin mevcut ilerlemesi. */
  progress(q: ActiveQuest, player: Player, discovered: number): number {
    switch (q.kind) {
      case 'gather':
      case 'mine':
      case 'craft':
        return player.inventory.count(q.target);
      case 'hunt':
      case 'fish':
        return q.counter;
      case 'explore':
        return Math.max(0, discovered - q.baseline);
      default:
        return 0;
    }
  }

  canComplete(q: ActiveQuest, player: Player, discovered: number): boolean {
    return !q.done && this.progress(q, player, discovered) >= q.amount;
  }

  /** Görevi teslim et: malzeme gerektiren türlerde envanterden düşülür. */
  complete(q: ActiveQuest, player: Player, discovered: number): { ok: boolean; message: string } {
    if (q.done) return { ok: false, message: 'Bu görev zaten teslim edildi' };
    if (!this.canComplete(q, player, discovered)) return { ok: false, message: 'Görev henüz tamamlanmadı' };

    // Eşya isteyen görevlerde teslimde eşya alınır (bedava ödül exploitini kapatır)
    if (q.kind === 'gather' || q.kind === 'mine' || q.kind === 'craft') {
      if (!player.inventory.remove(q.target, q.amount)) return { ok: false, message: 'Eşya teslim edilemedi' };
    }

    player.kogox += q.rewardKogox;
    const given: string[] = [];
    for (const r of q.rewardItems) {
      const left = player.inventory.add(r.item, r.count);
      if (left < r.count) given.push(`${itemDef(r.item).name} x${r.count - left}`);
    }
    q.done = true;
    this.state.completedToday++;
    const extra = given.length ? `, ${given.join(', ')}` : '';
    return { ok: true, message: `Görev tamam! +${q.rewardKogox} Kogox${extra}` };
  }

  toSave(): QuestState {
    return this.state;
  }

  loadSave(data: unknown): void {
    if (!data || typeof data !== 'object') return;
    const d = data as Partial<QuestState>;
    if (!Number.isFinite(d.day) || !Array.isArray(d.quests)) return;
    this.state = {
      day: d.day as number,
      completedToday: Number.isFinite(d.completedToday) ? (d.completedToday as number) : 0,
      quests: (d.quests as ActiveQuest[]).filter((q) => q && typeof q.id === 'string' && typeof q.target === 'string').slice(0, 6),
    };
  }
}
